import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Badge,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  CalendarToday,
  LocalFireDepartment,
} from '@mui/icons-material';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const StudyCalendar = ({ entries, onDateSelect }) => {
  const theme = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarDays, setCalendarDays] = useState([]);

  // Calculate calendar data
  useEffect(() => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayOfMonth = getFirstDayOfMonth(currentDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), i));
    }

    setCalendarDays(days);
  }, [currentDate]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePrevMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  };

  const getEntriesForDate = (date) => {
    if (!date || !entries) return [];
    return entries.filter(entry => {
      const entryDate = entry.timestamp?.toDate?.() || entry.date || new Date(entry.timestamp);
      return (
        entryDate.getDate() === date.getDate() &&
        entryDate.getMonth() === date.getMonth() &&
        entryDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const handleDateClick = (date) => {
    if (!date) return;
    setSelectedDate(date);
    const dateEntries = getEntriesForDate(date);
    onDateSelect(date, dateEntries);
  };

  const isSelected = (date) => {
    if (!date || !selectedDate) return false;
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Calendar navigation */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 2,
      }}>
        <IconButton onClick={handlePrevMonth}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6">
          {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
        </Typography>
        <IconButton onClick={handleNextMonth}>
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Calendar grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
        {/* Day headers */}
        {DAYS.map(day => (
          <Box 
            key={day}
            sx={{ 
              p: 1,
              textAlign: 'center',
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            {day}
          </Box>
        ))}

        {/* Calendar days */}
        {calendarDays.map((date, index) => {
          if (!date) {
            return (
              <Box 
                key={`empty-${index}`}
                sx={{ 
                  p: 1,
                  textAlign: 'center',
                  bgcolor: 'background.default',
                  borderRadius: 1,
                }}
              />
            );
          }

          const dateEntries = getEntriesForDate(date);
          const hasEntries = dateEntries.length > 0;

          return (
            <Box
              key={date.getTime()}
              onClick={() => handleDateClick(date)}
              sx={{
                p: 1,
                textAlign: 'center',
                cursor: 'pointer',
                borderRadius: 1,
                bgcolor: isSelected(date) 
                  ? `${theme.palette.primary.main}15`
                  : 'background.paper',
                border: isSelected(date)
                  ? `1px solid ${theme.palette.primary.main}`
                  : '1px solid transparent',
                '&:hover': {
                  bgcolor: `${theme.palette.primary.main}10`,
                },
              }}
            >
              {hasEntries ? (
                <Badge
                  color="primary"
                  badgeContent={dateEntries.length}
                  max={99}
                >
                  <Typography
                    variant="body2"
                    color={isSelected(date) ? 'primary' : 'text.primary'}
                  >
                    {date.getDate()}
                  </Typography>
                </Badge>
              ) : (
                <Typography
                  variant="body2"
                  color={isSelected(date) ? 'primary' : 'text.primary'}
                >
                  {date.getDate()}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default StudyCalendar; 